/**
 * F-004 — Planet entity (mesh + physics body).
 *
 * One Planet bundles a Three.js display mesh with a cannon-es Body so the
 * higher layers (game loop, collision merging, scoring) can address them as
 * a single object. ``syncMeshFromBody`` is called per frame to project the
 * physics state into the render scene.
 */

import { Body, Sphere as CannonSphere, Vec3 } from "cannon-es";
import {
  Mesh,
  MeshToonMaterial,
  SphereGeometry,
} from "three";

import { getTier, type PlanetTier } from "./tier.ts";
import { planetTexture } from "../render/planet-texture.ts";
import { createToonOutline } from "../render/toon-outline.ts";
import { createToonAtmosphere } from "../shaders/toon-atmosphere.ts";
import { createToonGradient } from "../shaders/toon-gradient.ts";

const SHARED_TOON_GRADIENT = createToonGradient(4);

export interface Vec3Like {
  x: number;
  y: number;
  z: number;
}

let nextPlanetId = 0;

export interface Planet {
  id: number;
  tier: PlanetTier;
  mesh: Mesh;
  body: Body;
  /** F-039 — radians per second around local Y. */
  spinRate: number;
  syncMeshFromBody(): void;
  destroy(): void;
}

export interface CreatePlanetOptions {
  tierIndex: number;
  position: Vec3Like;
  velocity?: Vec3Like;
}

export function createPlanet(opts: CreatePlanetOptions): Planet {
  const tier = getTier(opts.tierIndex);

  const geometry = new SphereGeometry(tier.radius, 48, 36);
  // Cartoon cel-shaded material — flat regions of color, hard step
  // transitions per the toon gradient. Vivid base color comes from
  // the per-tier hex.
  const material = new MeshToonMaterial({
    map: planetTexture(opts.tierIndex),
    color: tier.color,
    gradientMap: SHARED_TOON_GRADIENT,
  });
  const mesh = new Mesh(geometry, material);
  mesh.name = `planet-${tier.name}-${nextPlanetId}`;
  mesh.position.set(opts.position.x, opts.position.y, opts.position.z);

  // Cel-shaded silhouette around every planet. The thin dark hull is
  // what makes the cartoon language read at a glance. The cannon body
  // below uses the SAME outer radius so contact happens at the visible
  // silhouette edge — no clipping into the outline.
  const OUTLINE_SCALE = 1.045;
  const outline = createToonOutline({ planetRadius: tier.radius, scale: OUTLINE_SCALE });
  mesh.add(outline);
  const collisionRadius = tier.radius * OUTLINE_SCALE;

  // Per-tier cel-banded atmosphere. Earthier tiers get a brighter halo;
  // rocky inner worlds get a thin band. The toon-atmosphere shader
  // discretizes rim glow into cartoon rings rather than smooth Fresnel.
  const atmosphereParams = atmospherePresetForTier(opts.tierIndex);
  if (atmosphereParams) {
    const atmosphere = createToonAtmosphere({
      planetRadius: tier.radius,
      ...atmosphereParams,
    });
    mesh.add(atmosphere);
  }

  // (Saturn ring removed — meteor → Earth ladder no longer has Saturn.)

  const body = new Body({
    mass: tier.mass,
    shape: new CannonSphere(collisionRadius),
    position: new Vec3(opts.position.x, opts.position.y, opts.position.z),
    linearDamping: 0.04,
    angularDamping: 0.4,
  });
  if (opts.velocity) {
    body.velocity.set(opts.velocity.x, opts.velocity.y, opts.velocity.z);
  }

  const id = nextPlanetId;
  nextPlanetId += 1;

  // F-039 — per-planet self-rotation. Rate decreases with tier (gas
  // giants spin slowly; rocky inner worlds whip around faster). Axis
  // tilt randomized so adjacent planets don't look like clones.
  const spinRate = (0.45 + Math.random() * 0.55) * Math.max(0.4, 1 - opts.tierIndex * 0.07);
  const spinTiltX = (Math.random() - 0.5) * 0.7;
  const spinTiltZ = (Math.random() - 0.5) * 0.5;
  const spawnTimeMs = typeof performance !== "undefined" ? performance.now() : 0;

  let destroyed = false;

  const syncMeshFromBody = (): void => {
    if (destroyed) return;
    mesh.position.set(body.position.x, body.position.y, body.position.z);
    // F-039 — drive rotation off wall time so the spin is independent
    // of physics tumble (collisions on a sphere look like nothing
    // anyway, and we want a steady rotation).
    const elapsedSec =
      (typeof performance !== "undefined" ? performance.now() : 0) - spawnTimeMs;
    const t = elapsedSec / 1000;
    mesh.rotation.set(spinTiltX, t * spinRate, spinTiltZ);
  };

  const destroy = (): void => {
    if (destroyed) return;
    destroyed = true;
    geometry.dispose();
    material.dispose();
    (outline.geometry as SphereGeometry).dispose();
    (outline.material as { dispose(): void }).dispose();
    mesh.removeFromParent();
  };

  return { id, tier, mesh, body, spinRate, syncMeshFromBody, destroy };
}

export function __resetPlanetIdCounterForTests(): void {
  nextPlanetId = 0;
}

interface AtmospherePreset {
  color: number;
  intensity: number;
  bandCount: number;
  shellScale: number;
}

function atmospherePresetForTier(tierIndex: number): AtmospherePreset | null {
  // Meteors (rocky tiers) get a thin two-band halo. Larger bodies grow
  // more cel rings. Tier 10 (earth-sized) gets the most glow rings.
  switch (tierIndex) {
    case 0:
    case 1:
    case 2:
      return { color: 0xffb088, intensity: 0.55, bandCount: 2, shellScale: 1.08 };
    case 3:
      return { color: 0xc8f0ec, intensity: 0.85, bandCount: 3, shellScale: 1.10 };
    case 4:
      return { color: 0x88c8ff, intensity: 0.70, bandCount: 3, shellScale: 1.08 };
    case 5:
      return { color: 0xa0d8a8, intensity: 0.80, bandCount: 3, shellScale: 1.09 };
    case 6:
      return { color: 0xb0e090, intensity: 0.85, bandCount: 3, shellScale: 1.09 };
    case 7:
      return { color: 0x9cd8ff, intensity: 0.95, bandCount: 3, shellScale: 1.11 };
    case 8:
      return { color: 0x9cd8ff, intensity: 1.05, bandCount: 4, shellScale: 1.12 };
    case 9:
      return { color: 0x88c8ff, intensity: 1.20, bandCount: 4, shellScale: 1.12 };
    case 10:
      return { color: 0x6cb8ff, intensity: 1.55, bandCount: 4, shellScale: 1.13 };
    default:
      return null;
  }
}
